import React, { useCallback, useEffect, useState } from 'react';
import { X, FolderOpen, Trash2, Plus, Wifi, XCircle, Loader2 } from 'lucide-react';
import { btnStyle, CARD_INGEST_SPIN_KEYFRAMES } from './theme';

// Each ingest already generates a jobId (used for progress events during
// the run) -- this view is just a persisted list of those, so past ingests
// stay visible (with their Job ID, status, and file counts) after closing
// or reopening the window, the same way the proxy queue's own history does.
export default function WirelessReceiverPanel({ c, onClose, embedded = false }) {
  const [status, setStatus] = useState(null); // null = loading
  const [landingDir, setLandingDir] = useState('');
  const [port, setPort] = useState('2121');
  // One row per physical camera — each gets its own login and lands in its
  // own subfolder (see wirelessReceiver.js's start()). Username/password
  // left blank auto-generate from the name / a random value.
  const [cameraRows, setCameraRows] = useState([{ name: 'Camera 1', username: '', password: '' }]);
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState(null);
  const [receivedFiles, setReceivedFiles] = useState([]);

  const addCameraRow = useCallback(() => {
    setCameraRows(prev => [...prev, { name: `Camera ${prev.length + 1}`, username: '', password: '' }]);
  }, []);
  const removeCameraRow = useCallback((idx) => {
    setCameraRows(prev => prev.filter((_, i) => i !== idx));
  }, []);
  const setCameraField = useCallback((idx, key, value) => {
    setCameraRows(prev => prev.map((row, i) => (i === idx ? { ...row, [key]: value } : row)));
  }, []);

  const refreshStatus = useCallback(async () => {
    const result = await window.electronAPI.wireless.status();
    if (result?.success) {
      setStatus(result.data);
      if (result.data.receivedFiles) setReceivedFiles(result.data.receivedFiles);
    }
  }, []);

  useEffect(() => { refreshStatus(); }, [refreshStatus]);

  useEffect(() => {
    const offReceived = window.electronAPI.wireless.onFileReceived((entry) => {
      setReceivedFiles(prev => [entry, ...prev].slice(0, 200));
    });
    return () => offReceived?.();
  }, []);

  const chooseLandingDir = useCallback(async () => {
    const result = await window.electronAPI.wireless.chooseLandingDir();
    if (result?.success && result.path) setLandingDir(result.path);
  }, []);

  const start = useCallback(async () => {
    if (!landingDir || starting || cameraRows.length === 0) return;
    setStarting(true);
    setStartError(null);
    try {
      const result = await window.electronAPI.wireless.start({
        port: Number(port) || 2121, landingDir,
        cameras: cameraRows.map(row => ({
          name: row.name, username: row.username || undefined, password: row.password || undefined,
        })),
      });
      if (result?.success) {
        await refreshStatus();
      } else {
        setStartError(result?.error || 'Failed to start receiver');
      }
    } finally {
      setStarting(false);
    }
  }, [landingDir, port, cameraRows, starting, refreshStatus]);

  const stop = useCallback(async () => {
    await window.electronAPI.wireless.stop();
    await refreshStatus();
  }, [refreshStatus]);

  const inputStyle = {
    width: '100%', padding: '6px 9px', fontSize: '12px', borderRadius: '6px',
    border: `1px solid ${c.rule}`, background: c.bg, color: c.text, fontFamily: 'inherit',
    boxSizing: 'border-box',
  };
  const labelStyle = { fontSize: '10px', color: c.muted, marginBottom: '3px', display: 'block' };
  const running = status?.running;

  const content = (
    <>
      <style>{CARD_INGEST_SPIN_KEYFRAMES}</style>
      {!embedded && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
          <span style={{ fontSize: '13px', fontWeight: 700 }}>Wireless Footage Receiver</span>
          <button onClick={onClose} style={btnStyle(c, 'ghost')}><X size={12} /></button>
        </div>
      )}
        <div style={{ fontSize: '11px', color: c.muted, marginBottom: '14px', lineHeight: 1.5 }}>
          Cameras with built-in FTP transfer (Sony, Canon, and most professional bodies) can push each clip here automatically over WiFi right after it stops recording — no card, no cable. Point the camera's FTP settings at the details below.
        </div>

        {status === null && <div style={{ fontSize: '12px', color: c.muted }}>Loading…</div>}

        {status && !running && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div>
              <span style={labelStyle}>Landing folder</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ fontSize: '11px', color: c.muted, fontFamily: 'monospace', wordBreak: 'break-all', flex: 1 }}>{landingDir || '(not set)'}</div>
                <button onClick={chooseLandingDir} style={btnStyle(c, 'ghost')}><FolderOpen size={13} /> Choose</button>
              </div>
            </div>
            <div>
              <span style={labelStyle}>Port</span>
              <input value={port} onChange={e => setPort(e.target.value)} style={{ ...inputStyle, width: '120px' }} />
            </div>

            <div>
              <span style={labelStyle}>Cameras — one login per camera, each lands in its own subfolder</span>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {cameraRows.map((row, i) => (
                  <div key={i} style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                    <input value={row.name} onChange={e => setCameraField(i, 'name', e.target.value)} placeholder="Camera name" style={{ ...inputStyle, flex: 1.2 }} />
                    <input value={row.username} onChange={e => setCameraField(i, 'username', e.target.value)} placeholder="username (auto)" style={{ ...inputStyle, flex: 1 }} />
                    <input value={row.password} onChange={e => setCameraField(i, 'password', e.target.value)} placeholder="password (auto)" style={{ ...inputStyle, flex: 1 }} />
                    <button onClick={() => removeCameraRow(i)} disabled={cameraRows.length === 1} style={{ ...btnStyle(c, 'ghost'), opacity: cameraRows.length === 1 ? 0.4 : 1, flexShrink: 0 }} title="Remove camera">
                      <Trash2 size={12} />
                    </button>
                  </div>
                ))}
              </div>
              <button onClick={addCameraRow} style={{ ...btnStyle(c, 'ghost'), marginTop: '6px' }}>
                <Plus size={12} /> Add camera
              </button>
            </div>
            {startError && <div style={{ fontSize: '11px', color: c.err }}>{startError}</div>}
            <button onClick={start} disabled={!landingDir || starting} style={{ ...btnStyle(c, 'primary'), opacity: (!landingDir || starting) ? 0.5 : 1 }}>
              {starting ? <Loader2 size={12} style={{ animation: 'cardIngestSpin 1s linear infinite' }} /> : <Wifi size={13} />} Start Receiver
            </button>
          </div>
        )}

        {status && running && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <div style={{ padding: '12px', border: `1px solid ${c.ok}`, borderRadius: '8px', background: c.subtleBg }}>
              <div style={{ fontSize: '11px', color: c.ok, fontWeight: 700, marginBottom: '8px' }}>
                ● Receiving on your WiFi network — {status.cameras?.length || 0} camera{status.cameras?.length === 1 ? '' : 's'} configured
              </div>
              <div style={{ fontSize: '10.5px', color: c.muted, marginBottom: '6px' }}>Server: {status.lanIp}:{status.port} — enter each camera's own login below into that camera's FTP transfer settings.</div>
              {(status.cameras || []).map((cam) => (
                <div key={cam.username} style={{ fontSize: '12px', fontFamily: 'monospace', color: c.text, lineHeight: 1.7, paddingTop: '4px', borderTop: `1px solid ${c.rule}`, marginTop: '4px' }}>
                  <strong>{cam.name}</strong> — folder: {cam.folder}<br />
                  Username: {cam.username} · Password: {cam.password}
                </div>
              ))}
            </div>
            <button onClick={stop} style={btnStyle(c, 'ghost')}><XCircle size={13} /> Stop Receiver</button>

            <div>
              <div style={{ fontSize: '11px', fontWeight: 600, marginBottom: '6px' }}>Received files ({receivedFiles.length})</div>
              {receivedFiles.length === 0 && <div style={{ fontSize: '11px', color: c.muted }}>Nothing received yet.</div>}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', maxHeight: '200px', overflowY: 'auto' }}>
                {receivedFiles.map((f, i) => (
                  <div key={`${f.fileName}-${f.receivedAt}-${i}`} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', padding: '4px 0', borderBottom: `1px solid ${c.rule}` }}>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                      {f.camera && <span style={{ color: c.muted }}>[{f.camera}]</span>} {f.fileName}
                    </span>
                    <span style={{ color: c.muted, flexShrink: 0, marginLeft: '8px' }}>{new Date(f.receivedAt).toLocaleTimeString()}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
    </>
  );

  if (embedded) return <div style={{ width: '100%' }}>{content}</div>;

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ width: 480, maxHeight: '80vh', overflow: 'auto', background: c.bg, borderRadius: '10px', border: `1px solid ${c.rule}`, padding: '18px 20px' }}
      >
        {content}
      </div>
    </div>
  );
}
